/**
 * Fast Astronomical Object Search Engine
 * 
 * This module provides lightning-fast search capabilities for astronomical objects
 * using the IndexedDB database with intelligent ranking and fuzzy matching.
 */

import DatabaseManager from './databaseManager.js';

// localStorage key holding the data version the cached IndexedDB copy came from
export const DATA_VERSION_KEY = 'astro_data_version';

/**
 * Decide whether the cached database must be (re)loaded from the published JSON.
 * An empty cache always reloads; otherwise reload only when a published version
 * exists and differs from the one the cache was loaded from.
 * @param {{objectCount: number, aliasCount: number, storedVersion: ?string, publishedVersion: ?string}} state
 * @returns {boolean}
 */
export function needsDataReload({ objectCount, aliasCount, storedVersion, publishedVersion }) {
    if (!objectCount || !aliasCount) return true;
    if (!publishedVersion) return false; // no version file published: keep the cache
    return storedVersion !== publishedVersion;
}

export class FastAstroSearch {
    constructor() {
        this.dbManager = new DatabaseManager();
        this.isInitialized = false;
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes

        // In-memory search indexes (built once at import time)
        this.objectMap = new Map();       // id → object
        this.nameToId = new Map();        // lowercase name/alias → object id
        this.aliasToObjectName = new Map(); // lowercase alias → object name (for resolving)
        this.catalogIndex = new Map();    // "ngc" → Map(number → id), "messier" → Map(number → id)
        this.allAliasKeys = [];           // sorted alias keys for prefix search
        this.indexBuilt = false;
    }

    /**
     * Initialize the search engine
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        if (this.isInitialized) {
            return true;
        }

        try {
            await this.dbManager.initialize();
            this.isInitialized = true;
            console.log('Fast search engine initialized');
            return true;
        } catch (error) {
            console.error('Failed to initialize search engine:', error);
            return false;
        }
    }

    /**
     * Search for astronomical objects with intelligent ranking
     * @param {string} query - Search query
     * @param {number} maxResults - Maximum number of results
     * @returns {Promise<Array>} Array of search results
     */
    async searchObjects(query, maxResults = 15) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (!query || query.trim().length < 1) {
            return [];
        }

        const trimmedQuery = query.trim();

        // Check cache first
        const cacheKey = `${trimmedQuery}_${maxResults}`;
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                return cached.results;
            }
        }

        try {
            const results = await this.performSearch(trimmedQuery, maxResults);

            // Cache the results
            this.cache.set(cacheKey, {
                results: results,
                timestamp: Date.now()
            });

            return results;
        } catch (error) {
            console.error('Search error:', error);
            return [];
        }
    }

    /**
     * Search for objects within a direction cone in the sky
     * @param {number} centerRA - Center right ascension in hours
     * @param {number} centerDec - Center declination in degrees
     * @param {number} radius - Search radius in degrees
     * @param {Object} filters - Optional filters (magnitude, object type, etc.)
     * @returns {Promise<Array>} Array of objects within the cone
     */
    async searchByDirection(centerRA, centerDec, radius, filters = {}) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            // Get all objects from the database
            const allObjects = await this.getAllObjects();
            console.log(`Searching ${allObjects.length} objects for direction RA=${centerRA.toFixed(3)}h, Dec=${centerDec.toFixed(1)}°, radius=${radius}°`);
            const objectsInCone = [];

            for (const obj of allObjects) {
                // Skip objects without coordinates
                if (!obj.ra_hours || !obj.dec_degrees) {
                    continue;
                }

                // Calculate angular distance from center
                const distance = this.calculateAngularDistance(
                    centerRA,
                    centerDec,
                    obj.ra_hours,
                    obj.dec_degrees
                );

                // Check if within search radius
                if (distance <= radius) {
                    // Apply additional filters if specified
                    if (this.passesFilters(obj, filters)) {
                        objectsInCone.push({
                            ...obj,
                            distanceFromCenter: distance
                        });
                    }
                }
            }

            // Sort by distance from center (closest first)
            objectsInCone.sort((a, b) => a.distanceFromCenter - b.distanceFromCenter);

            return objectsInCone;

        } catch (error) {
            console.error('Direction search error:', error);
            return [];
        }
    }

    /**
     * Calculate angular distance between two celestial coordinates
     * @param {number} ra1 - Right ascension 1 in hours
     * @param {number} dec1 - Declination 1 in degrees
     * @param {number} ra2 - Right ascension 2 in hours
     * @param {number} dec2 - Declination 2 in degrees
     * @returns {number} Angular distance in degrees
     */
    calculateAngularDistance(ra1, dec1, ra2, dec2) {
        // Convert RA from hours to degrees
        const ra1Deg = ra1 * 15.0;
        const ra2Deg = ra2 * 15.0;

        // Convert to radians
        const ra1Rad = ra1Deg * Math.PI / 180.0;
        const dec1Rad = dec1 * Math.PI / 180.0;
        const ra2Rad = ra2Deg * Math.PI / 180.0;
        const dec2Rad = dec2 * Math.PI / 180.0;

        // Use the haversine formula for great circle distance
        const deltaRA = ra2Rad - ra1Rad;
        const deltaDec = dec2Rad - dec1Rad;

        const a = Math.sin(deltaDec / 2) * Math.sin(deltaDec / 2) +
                  Math.cos(dec1Rad) * Math.cos(dec2Rad) *
                  Math.sin(deltaRA / 2) * Math.sin(deltaRA / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        // Convert result from radians to degrees
        return c * 180.0 / Math.PI;
    }

    /**
     * Check if an object passes the specified filters
     * @param {Object} obj - Astronomical object
     * @param {Object} filters - Filter criteria
     * @returns {boolean} True if object passes all filters
     */
    passesFilters(obj, filters) {
        // Magnitude filter
        if (filters.minMagnitude !== undefined && obj.magnitude !== null && obj.magnitude < filters.minMagnitude) {
            return false;
        }
        if (filters.maxMagnitude !== undefined && obj.magnitude !== null && obj.magnitude > filters.maxMagnitude) {
            return false;
        }

        // Object type filter
        if (filters.objectTypes && filters.objectTypes.length > 0) {
            if (!obj.object_type || !filters.objectTypes.includes(obj.object_type)) {
                return false;
            }
        }

        // Catalog filter
        if (filters.catalogs && filters.catalogs.length > 0) {
            if (!obj.catalog || !filters.catalogs.includes(obj.catalog)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Get all objects from the database (with coordinate conversion)
     * @returns {Promise<Array>} All astronomical objects
     */
    async getAllObjects() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            // Use in-memory index if available
            let allData;
            if (this.indexBuilt && this.objectMap.size > 0) {
                allData = Array.from(this.objectMap.values());
            } else {
                allData = await this.dbManager.exportData();
                // Build index if not yet built (e.g. loaded from existing IndexedDB)
                if (!this.indexBuilt && allData.length > 0) {
                    await this.buildIndexFromDatabase(allData);
                }
            }

            // Convert coordinates to expected format
            return allData.map(obj => ({
                ...obj,
                ra_hours: obj.ra_deg ? obj.ra_deg / 15.0 : obj.ra_hours,
                dec_degrees: obj.dec_deg !== undefined ? obj.dec_deg : obj.dec_degrees
            }));
        } catch (error) {
            console.error('Error getting all objects:', error);
            return [];
        }
    }

    /**
     * Perform the actual search using pre-built alias indexes.
     * No longer loads all objects — uses O(1) lookups and targeted scans.
     */
    async performSearch(query, maxResults) {
        // Fallback: if index not built yet, build from IndexedDB data
        if (!this.indexBuilt) {
            await this.buildIndexFromDatabase();
        }

        const queryLower = query.toLowerCase();
        const queryUpper = query.toUpperCase();
        const searchResults = new Map(); // id → { object, score }

        const addResult = (id, baseScore) => {
            const obj = this.objectMap.get(id);
            if (!obj) return;
            const score = baseScore + this.calculateBonusScore(obj);
            const existing = searchResults.get(id);
            if (!existing || existing.score < score) {
                searchResults.set(id, { object: obj, score });
            }
        };

        // Strategy 1: Exact alias/name lookup — O(1)
        const exactId = this.nameToId.get(queryLower);
        if (exactId) addResult(exactId, 1000);

        // Strategy 2: Catalog-specific lookup — O(1)
        this.catalogLookup(queryUpper, addResult);

        // Strategy 3: Prefix matches on alias keys — binary search + scan
        if (searchResults.size < maxResults) {
            this.prefixSearch(queryLower, searchResults, addResult, maxResults, 800);
        }

        // Strategy 4: Contains matches — scan alias keys (only if few results)
        if (searchResults.size < maxResults && queryLower.length >= 3) {
            this.containsSearch(queryLower, searchResults, addResult, maxResults, 600);
        }

        // Strategy 5: Fuzzy variations
        if (searchResults.size < maxResults) {
            const variations = this.generateQueryVariations(queryLower);
            for (const variation of variations) {
                const varLower = variation.toLowerCase();
                if (varLower === queryLower) continue;
                const varId = this.nameToId.get(varLower);
                if (varId) addResult(varId, 400);
            }
        }

        return Array.from(searchResults.values())
            .sort((a, b) => b.score - a.score)
            .slice(0, maxResults)
            .map(item => item.object);
    }

    /**
     * O(1) catalog + number lookup using catalogIndex
     */
    catalogLookup(queryUpper, addResult) {
        const patterns = [
            { re: /^M\s*(\d+)$/i, catalog: 'messier' },
            { re: /^NGC\s*(\d+)$/i, catalog: 'ngc' },
            { re: /^IC\s*(\d+)$/i, catalog: 'ic' },
            { re: /^C\s*(\d+)$/i, catalog: 'caldwell' },
            { re: /^SH\s*2?[-\s]*(\d+)$/i, catalog: 'sharpless' },
            { re: /^RCW\s*(\d+)$/i, catalog: 'rcw' },
            { re: /^CR\s*(\d+)$/i, catalog: 'collinder' },
            { re: /^B\s*(\d+)$/i, catalog: 'barnard' },
            { re: /^LDN\s*(\d+)$/i, catalog: 'ldn' },
            { re: /^VDB\s*(\d+)$/i, catalog: 'vdb' },
            { re: /^GUM\s*(\d+)$/i, catalog: 'gum' },
            { re: /^ABELL\s*(\d+)$/i, catalog: 'abell' },
        ];

        for (const { re, catalog } of patterns) {
            const match = queryUpper.match(re);
            if (match) {
                const catMap = this.catalogIndex.get(catalog);
                if (catMap) {
                    const id = catMap.get(match[1]);
                    if (id) addResult(id, 900);
                }
            }
        }

        // Pure number → try NGC first, then Messier
        if (/^\d+$/.test(queryUpper) && queryUpper.length >= 2) {
            const ngcMap = this.catalogIndex.get('ngc');
            if (ngcMap) {
                const id = ngcMap.get(queryUpper);
                if (id) addResult(id, 850);
            }
            const mMap = this.catalogIndex.get('messier');
            if (mMap) {
                const id = mMap.get(queryUpper);
                if (id) addResult(id, 850);
            }
        }
    }

    /**
     * Binary search for prefix matches in sorted alias keys array
     */
    prefixSearch(queryLower, searchResults, addResult, maxResults, baseScore) {
        // Binary search to find first key >= queryLower
        let lo = 0, hi = this.allAliasKeys.length;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (this.allAliasKeys[mid] < queryLower) lo = mid + 1;
            else hi = mid;
        }

        // Scan forward while keys start with query
        for (let i = lo; i < this.allAliasKeys.length && searchResults.size < maxResults * 3; i++) {
            const key = this.allAliasKeys[i];
            if (!key.startsWith(queryLower)) break;
            if (key === queryLower) continue; // already handled by exact match
            const id = this.nameToId.get(key);
            if (id) addResult(id, baseScore);
        }
    }

    /**
     * Substring search across alias keys (expensive, only used as fallback)
     */
    containsSearch(queryLower, searchResults, addResult, maxResults, baseScore) {
        const limit = maxResults * 3;
        let found = 0;
        for (const key of this.allAliasKeys) {
            if (found >= limit) break;
            if (key.includes(queryLower) && !key.startsWith(queryLower)) {
                const id = this.nameToId.get(key);
                if (id && !searchResults.has(id)) {
                    addResult(id, baseScore);
                    found++;
                }
            }
        }
    }

    /**
     * Generate query variations for fuzzy matching
     * @param {string} query - Original query
     * @returns {Array} Array of variations
     */
    generateQueryVariations(query) {
        const variations = [query, query.toUpperCase(), query.toLowerCase()];
        const queryLower = query.toLowerCase();
        
        // Handle common Messier variations
        if (queryLower.startsWith('m')) {
            const num = query.match(/m\s*(\d+)/i);
            if (num) {
                variations.push(`MESSIER ${num[1].padStart(3, '0')}`);
                variations.push(`M${num[1]}`);
                variations.push(`M ${num[1]}`);
            }
        }
        
        // Handle NGC variations
        if (queryLower.startsWith('ngc')) {
            const num = query.match(/ngc\s*(\d+)/i);
            if (num) {
                variations.push(`NGC ${num[1]}`);
                variations.push(`NGC${num[1]}`);
            }
        }
        
        // Handle IC variations
        if (queryLower.startsWith('ic')) {
            const num = query.match(/ic\s*(\d+)/i);
            if (num) {
                variations.push(`IC ${num[1]}`);
                variations.push(`IC${num[1]}`);
            }
        }
        
        // Handle Sharpless variations
        if (queryLower.includes('sh')) {
            const num = query.match(/sh\s*2?[-\s]*(\d+)/i);
            if (num) {
                variations.push(`SH2-${num[1]}`);
                variations.push(`SH 2-${num[1]}`);
                variations.push(`SHARPLESS ${num[1]}`);
            }
        }
        
        // Handle number-only queries (try all major catalogs)
        if (/^\d+$/.test(query)) {
            variations.push(`NGC ${query}`);
            variations.push(`M${query}`);
            variations.push(`IC ${query}`);
            variations.push(`MESSIER ${query.padStart(3, '0')}`);
        }
        
        return [...new Set(variations)];
    }

    /**
     * Calculate bonus score for object properties
     * @param {Object} obj - Astronomical object
     * @returns {number} Bonus score
     */
    calculateBonusScore(obj) {
        let bonus = 0;
        
        // Bonus for popular catalogs
        if (obj.catalog) {
            const catalog = obj.catalog.toLowerCase();
            if (['messier', 'ngc', 'ic'].includes(catalog)) {
                bonus += 100;
            } else if (['sharpless', 'caldwell'].includes(catalog)) {
                bonus += 50;
            }
        }
        
        // Bonus for brighter objects
        if (obj.magnitude !== null && obj.magnitude !== undefined) {
            bonus += 20;
            if (obj.magnitude < 10) bonus += 10;
            if (obj.magnitude < 5) bonus += 20;
        }
        
        // Bonus for having description
        if (obj.description && obj.description.trim().length > 0) {
            bonus += 10;
        }
        
        return bonus;
    }

    /**
     * Get object by name with alias-aware lookup
     * @param {string} name - Object name or alias
     * @returns {Promise<Object|null>} Object or null
     */
    async getObjectByName(name) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        // O(1) lookup via alias index
        if (this.indexBuilt) {
            const id = this.nameToId.get(name.toLowerCase());
            if (id) {
                const obj = this.objectMap.get(id);
                if (obj) return obj;
            }
        }

        // Fallback to IndexedDB exact match
        const result = await this.dbManager.getObjectByName(name);
        if (result) return result;

        // Fallback to search
        const searchResults = await this.searchObjects(name, 1);
        return searchResults.length > 0 ? searchResults[0] : null;
    }

    /**
     * Get objects by catalog
     * @param {string} catalog - Catalog name
     * @returns {Promise<Array>} Array of objects
     */
    async getObjectsByCatalog(catalog) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return await this.dbManager.getObjectsByCatalog(catalog);
    }

    /**
     * Get database statistics
     * @returns {Promise<Object>} Database statistics
     */
    async getStats() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return await this.dbManager.getStats();
    }

    /**
     * Make sure the cached database matches the published data: load it when the
     * cache is empty or when ./data/data_version.json reports a newer version.
     * @param {{onReloadStart?: Function}} [options] - onReloadStart is called before a
     *   (slow, ~30 s) reload begins so the page can show progress
     * @returns {Promise<boolean>} true if data was (re)loaded
     */
    async ensureDataLoaded({ onReloadStart } = {}) {
        const stats = await this.getStats();

        let publishedVersion = null;
        try {
            const response = await fetch('./data/data_version.json', { cache: 'no-store' });
            if (response.ok) publishedVersion = (await response.json()).version || null;
        } catch (_) {
            // No version file (or offline): fall back to the empty-cache check only
        }

        let storedVersion = null;
        try { storedVersion = localStorage.getItem(DATA_VERSION_KEY); } catch (_) { /* storage blocked */ }

        if (!needsDataReload({ objectCount: stats.totalObjects, aliasCount: stats.totalAliases, storedVersion, publishedVersion })) {
            this.objectCount = stats.totalObjects;
            console.log(`Database up to date (${stats.totalObjects} objects, version ${storedVersion || 'unversioned'})`);
            return false;
        }

        console.log(`Loading astronomical objects (published version ${publishedVersion || 'unversioned'})...`);
        if (onReloadStart) onReloadStart();
        const bust = publishedVersion ? `?v=${encodeURIComponent(publishedVersion)}` : '';
        const objectsResponse = await fetch(`./data/astro_objects.json${bust}`);
        if (!objectsResponse.ok) {
            throw new Error('Failed to load objects data');
        }
        const objects = await objectsResponse.json();

        const aliasesResponse = await fetch(`./data/object_aliases.json${bust}`);
        const aliases = aliasesResponse.ok ? await aliasesResponse.json() : [];

        await this.importData(objects, aliases);
        this.objectCount = objects.length;

        if (publishedVersion) {
            try { localStorage.setItem(DATA_VERSION_KEY, publishedVersion); } catch (_) { /* storage blocked */ }
        }
        console.log(`Loaded ${objects.length} objects and ${aliases.length} aliases`);
        return true;
    }

    /**
     * Import data from JSON files and build search indexes
     * @param {Array} objects - Array of objects to import
     * @param {Array} aliases - Array of aliases to import (optional)
     * @returns {Promise<number>} Number of objects imported
     */
    async importData(objects, aliases = []) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        // Clear existing data first
        await this.dbManager.clearDatabase();

        // Import objects and aliases into IndexedDB
        const imported = await this.dbManager.importData(objects, aliases);

        // Build in-memory search indexes
        this.buildSearchIndex(objects, aliases);

        return imported;
    }

    /**
     * Build the in-memory index from the cached IndexedDB data, including aliases,
     * so common names ("Orion Nebula") resolve on visits that didn't import the data.
     * @param {Array} [objects] - Objects already read from the database
     */
    async buildIndexFromDatabase(objects = null) {
        const allObjects = objects || await this.dbManager.exportData();
        let aliasRows = [];
        try {
            aliasRows = await this.dbManager.exportAliases();
        } catch (error) {
            console.warn('Could not read aliases from the database; searching names only', error);
        }

        const nameById = new Map(allObjects.map((obj) => [obj.id, obj.name]));
        const aliases = [];
        for (const row of aliasRows) {
            const objectName = nameById.get(row.object_id);
            if (objectName) aliases.push({ alias: row.alias_name, object_name: objectName });
        }

        this.buildSearchIndex(allObjects, aliases);
    }

    /**
     * Build in-memory search indexes for fast alias-aware search.
     * Called once at import time. Maps every alias/name to its object.
     */
    buildSearchIndex(objects, aliases) {
        const start = performance.now();
        this.objectMap.clear();
        this.nameToId.clear();
        this.catalogIndex.clear();

        const nameToObject = new Map(); // object.name → object (for alias resolution)

        // Index all objects
        for (const obj of objects) {
            this.objectMap.set(obj.id, obj);
            nameToObject.set(obj.name, obj);

            const nameLower = obj.name.toLowerCase();
            this.nameToId.set(nameLower, obj.id);

            if (obj.display_name) {
                this.nameToId.set(obj.display_name.toLowerCase(), obj.id);
            }

            // Build catalog index for O(1) catalog+number lookups
            if (obj.catalog && obj.catalog_number) {
                const catKey = obj.catalog.toLowerCase();
                if (!this.catalogIndex.has(catKey)) {
                    this.catalogIndex.set(catKey, new Map());
                }
                this.catalogIndex.get(catKey).set(obj.catalog_number, obj.id);
            }
        }

        // Index all aliases
        for (const aliasData of aliases) {
            const key = aliasData.alias.toLowerCase();
            if (!this.nameToId.has(key)) {
                // Resolve alias → object via object_name
                const obj = nameToObject.get(aliasData.object_name);
                if (obj) {
                    this.nameToId.set(key, obj.id);
                }
            }
        }

        // Build sorted alias keys for efficient prefix search
        this.allAliasKeys = Array.from(this.nameToId.keys()).sort();

        this.indexBuilt = true;
        const elapsed = performance.now() - start;
        console.log(`Search index built: ${this.nameToId.size} entries in ${elapsed.toFixed(0)}ms`);
    }

    /**
     * Clear search cache
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache statistics
     */
    getCacheStats() {
        return {
            size: this.cache.size,
            timeout: this.cacheTimeout
        };
    }
}

export default FastAstroSearch;

